import React from 'react';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

interface MultiCompanyLineChartProps {
  data: Array<{
    date: string;
    [companyName: string]: string | number | undefined;
  }>;
  title?: string;
  companies: Array<{
    name: string;
    color: string;
  }>;
  height?: number;
  selectedCompany?: string;
}

// Generate distinct colors for companies - more vibrant and distinct
const generateColors = (count: number): string[] => {
  const colors = [
    '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
    '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf',
    '#aec7e8', '#ffbb78', '#98df8a', '#ff9896', '#c5b0d5',
    '#c49c94', '#f7b6d3', '#c7c7c7', '#dbdb8d', '#9edae5',
    '#393b79', '#637939', '#8c6d31', '#843c39', '#7b4173',
    '#5254a3', '#637939', '#8c6d31', '#bd9e39', '#ad494a',
    '#6b6ecf', '#b5cf6b', '#cedb9c', '#e7ba52', '#e7969c',
  ];
  // If we need more colors, generate them using HSL
  if (count > colors.length) {
    for (let i = colors.length; i < count; i++) {
      const hue = (i * 137.508) % 360; // Golden angle approximation for good distribution
      colors.push(`hsl(${hue}, 70%, 50%)`);
    }
  }
  return colors.slice(0, count);
};

export const MultiCompanyLineChart: React.FC<MultiCompanyLineChartProps> = ({
  data,
  title,
  companies,
  height = 600,
  selectedCompany,
}) => {
  const colors = generateColors(companies.length);
  
  // Custom tooltip to show company name clearly, with selected company first, then sorted by value descending
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      // Filter out null values
      const validEntries = payload.filter((entry: any) => entry.value != null);
      
      // Separate selected company from others
      const selectedEntry = selectedCompany 
        ? validEntries.find((entry: any) => entry.name === selectedCompany)
        : null;
      
      const otherEntries = validEntries.filter(
        (entry: any) => !selectedCompany || entry.name !== selectedCompany
      );
      
      // Sort other entries by value in descending order
      otherEntries.sort((a: any, b: any) => (b.value || 0) - (a.value || 0));
      
      // Combine: selected company first (if exists), then others sorted descending
      const sortedEntries = selectedEntry 
        ? [selectedEntry, ...otherEntries]
        : otherEntries;
      
      return (
        <div style={{
          backgroundColor: '#fff',
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '10px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        }}>
          {sortedEntries.map((entry: any, index: number) => {
            const isSelected = selectedCompany && entry.name === selectedCompany;
            return (
              <p key={index} style={{ 
                margin: '4px 0', 
                color: entry.color,
                fontWeight: isSelected ? 700 : 400,
                fontSize: isSelected ? '14px' : '13px',
              }}>
                <span style={{ fontWeight: 600 }}>{entry.name}:</span> {entry.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };
  
  return (
    <div style={{ width: '100%', height }}>
      {title && <h3 style={{ marginBottom: '16px' }}>{title}</h3>}
      <ResponsiveContainer width="100%" height="100%">
        <RechartsLineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis 
            dataKey="date" 
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis />
          <Tooltip content={<CustomTooltip />} />
          {companies.map((company, index) => {
            const isSelected = selectedCompany && company.name === selectedCompany;
            return (
              <Line
                key={company.name}
                type="monotone"
                dataKey={company.name}
                name={company.name}
                stroke={company.color || colors[index]}
                strokeWidth={isSelected ? 4 : 2.5}
                dot={isSelected ? { r: 4, fill: company.color || colors[index] } : false}
                connectNulls={false}
                activeDot={{ r: isSelected ? 8 : 6 }}
                strokeDasharray={isSelected ? undefined : undefined}
              />
            );
          })}
        </RechartsLineChart>
      </ResponsiveContainer>
    </div>
  );
};


